import Page from '../Page/Page';
import styles from './BecomeAPro.module.css';
import React from "react";
import { useIntl } from '../../util/reactIntl';
import { LayoutSingleColumn } from '../LayoutComposer';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

const BecameAProPage = () => {
    const intl = useIntl();
    const schemaTitle = intl.formatMessage(
        { id: 'ListingPage.schemaTitle' },
        { title: 'BecomeAPro' }
    );

    const whyJoinFeatures = [
        {
            icon: "👥",
            title: "Access Quality Clients",
            description: "Connect with homeowners and businesses actively seeking environmental services and solutions."
        },
        {
            icon: "📈",
            title: "Grow Your Business",
            description: "Increase your visibility and revenue by showcasing your expertise to our engaged user base."
        },
        {
            icon: "🛡️",
            title: "Secure Payments",
            description: "Get paid safely and on time with our secure platform payment processing system."
        },
        {
            icon: "🏅",
            title: "Build Your Reputation",
            description: "Earn reviews and ratings that showcase your work quality and professionalism."
        },
        {
            icon: "⏰",
            title: "Flexible Schedule",
            description: "Work on your own terms and choose the projects that fit your availability and expertise."
        },
        {
            icon: "📢",
            title: "Marketing Support",
            description: "Benefit from our marketing efforts to bring quality leads directly to your profile."
        }
    ];

    const howItWorksSteps = [
        {
            icon: "👤",
            title: "Sign Up & Verify",
            description: "Register on Envirogigs by submitting your background information, credentials, insurance, and certifications. Verification builds trust and protects both providers and customers."
        },
        {
            icon: "📄",
            title: "Create a Listing",
            description: "Once approved, complete your profile and publish service listings on Envirogigs. Customers can browse, compare providers, and book the right expert for their needs."
        },
        {
            icon: "💬",
            title: "Messaging & Leads",
            description: "All providers can use in-app messaging to connect with customers for free. Upgrade to our lead service to receive direct job requests, manage inquiries, and grow opportunities."
        },
        {
            icon: "💰",
            title: "Get Booked & Paid",
            description: "Customers can book directly or after you submit an invoice in the app. Payments are held securely until the job is complete, then released minus a Service & Trust Fee."
        }
    ];

    const specialties = [
        "Mold Inspectors & Remediators",
        "Asbestos & Lead Abatement Specialists",
        "Water Damage & Fire Restoration Professionals",
        "Industrial Hygienists",
        "Hazardous Waste Removal Specialists",
        "Air Quality & Radon Testing Providers",
        "Environmental Engineers & Consultants",
        "Safety Trainers & Compliance Experts",
        "Pest Control Specialists",
        "Green Building & Energy Auditors",
        "General Contractors"
    ];

    const requirements = [
        {
            icon: "📋",
            title: "Valid License & Certifications",
            description: "Must hold current licenses and certifications relevant to your service area."
        },
        {
            icon: "🛡️",
            title: "Liability Insurance",
            description: "Proof of current general liability insurance coverage."
        },
        {
            icon: "👨‍💼",
            title: "Professional Experience",
            description: "Demonstrated experience in environmental services or related fields."
        },
        {
            icon: "✅",
            title: "Background Check",
            description: "Successful completion of our verification and background screening process."
        },
        {
            icon: "🏥",
            title: "Safety & Health Standards",
            description: "Commitment to following industry safety protocols and health guidelines."
        }
    ];

    return (
        <Page
            title='BecomeAPro'
            scrollingDisabled={false}
        >
            <LayoutSingleColumn
                mainColumnClassName={styles.layoutWrapperMain}
                topbar={<TopbarContainer />}
                footer={<FooterContainer />}
            >
                <div className={styles.pageWrapper}>
                    {/* Hero Section */}
                    <section className={styles.heroSection}>
                        <div className={styles.heroContent}>
                            <div className={styles.heroLeft}>
                                <span className={styles.heroTag}>For Environmental Pros</span>
                                <h1 className={styles.heroTitle}>Become an<br /> EnviroPro</h1>
                                <p className={styles.heroDescription}>
                                    Get paid for environmental work that matters. Take projects, book walkthroughs, and manage jobs in one place.
                                </p>
                                <ul className={styles.heroFeatures}>
                                    <li>✓ Pick projects that fit your skills</li>
                                    <li>✓ Milestone-based payouts</li>
                                    <li>✓ One profile for all clients</li>
                                </ul>
                                <div className={styles.heroButtons}>
                                    <button className={styles.btnPrimary}>Apply as an EnviroPro</button>
                                    <button className={styles.btnSecondary}>How it works</button>
                                </div>
                            </div>
                            <div className={styles.heroRight}>
                                <div className={styles.dashboardCard}>
                                    <div className={styles.dashboardHeader}>
                                        <h3>EnviroPro Dashboard</h3>
                                        <div className={styles.notification}>
                                            Walkthrough booked Tue at 10:30 AM
                                        </div>
                                    </div>
                                    <div className={styles.dashboardStats}>
                                        <div className={styles.stat}>
                                            <div className={styles.statLabel}>Earnings this month</div>
                                            <div className={styles.statValue}>$4,320</div>
                                        </div>
                                        <div className={styles.stat}>
                                            <div className={styles.statLabel}>Active jobs</div>
                                            <div className={styles.statValue}>3</div>
                                        </div>
                                    </div>
                                    <div className={styles.dashboardMap}>
                                        <div className={styles.mapPin}>📍 Lead</div>
                                        <div className={styles.mapPin}>📍 Walkthrough</div>
                                        <div className={styles.mapPin}>📍 Active job</div>
                                    </div>
                                    <div className={styles.jobInvite}>
                                        <h4>New Job Invite</h4>
                                        <div className={styles.jobDetails}>
                                            <span>IAG Assessment</span>
                                            <span className={styles.jobPrice}>$3800</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.rating}>
                                    <span className={styles.stars}>★★★★★</span>
                                    <span>Clients rated you 4.9/5</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Join Our Network Section */}
                    <section className={styles.joinNetworkSection}>
                        <div className={styles.joinNetworkContent}>
                            <div className={styles.joinNetworkLeft}>
                                <h2 className={styles.sectionTitle}>Join Our Network</h2>
                                <h3 className={styles.sectionSubtitle}>Join a Trusted Network of<br /> Environmental and Safety Professionals</h3>
                                <p className={styles.sectionDescription}>
                                    At EnviroGigs, we're building a marketplace where skilled providers connect with clients who need environmental, safety, and compliance services. By joining our network, you'll gain access to new opportunities, showcase your expertise, and grow your business, all while helping homes, businesses, and communities stay safe and compliant.
                                </p>
                            </div>
                            <div className={styles.joinNetworkRight}>
                                <div className={styles.professionalImage}>
                                    {/* Placeholder for professional image */}
                                    <div className={styles.imagePlaceholder}>
                                        <img src="/manInBlack.abif" alt="Professional" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Why Join Envirogigs Section */}
                    <section className={styles.whyJoinSection}>
                        <div className={styles.sectionContainer}>
                            <h2 className={styles.sectionTitle}>Why Join Envirogigs?</h2>
                            <p className={styles.sectionIntro}>
                                Access a growing marketplace of environmentally conscious clients and grow your business<br /> with our support.
                            </p>
                            <div className={styles.featuresGrid}>
                                {whyJoinFeatures.map((feature, index) => (
                                    <div key={index} className={styles.featureCard}>
                                        <div className={styles.featureIcon}>{feature.icon}</div>
                                        <p className={styles.featureTitle}>{feature.title}</p>
                                        <p className={styles.featureDescription}>{feature.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Worker on Roof Section */}
                    <section className={styles.imageTextSection}>
                        <div className={styles.imageTextContent}>
                            <div className={styles.imageTextImage}>
                                <div className={styles.imagePlaceholder}>Worker on Roof</div>
                            </div>
                            <div className={styles.imageTextBox}>
                                <h2 className={styles.imageTextTitle}>Your Work Protects Lives & Communities</h2>
                                <p className={styles.imageTextDescription}>
                                    As an environmental professional on our platform, your expertise directly safeguards families from hidden hazards, helps businesses maintain healthy workspaces, and protects entire communities from environmental risks. From identifying dangerous contaminants to implementing life-saving remediation solutions, your work creates safer homes, healthier environments, and peace of mind for those who need it most.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* How it Works Section */}
                    <section className={styles.howItWorksSection}>
                        <div className={styles.sectionContainer}>
                            <h3 className={styles.sectionTitle}>How it Works?</h3>
                            <div className={styles.stepsGrid}>
                                {howItWorksSteps.map((step, index) => (
                                    <div key={index} className={styles.stepCard}>
                                        <div className={styles.stepIcon}>{step.icon}</div>
                                        <p className={styles.stepTitle}>{step.title}</p>
                                        <p className={styles.stepDescription}>{step.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Who Should Join Section */}
                    <section className={styles.whoShouldJoinSection}>
                        <div className={styles.sectionContainer}>
                            <h2 className={styles.sectionTitle}>Who Should Join Envirogigs?</h2>
                            <p className={styles.sectionIntro}>
                                We're looking for certified environmental professionals across all specialties who are passionate about protecting health and safety.
                            </p>
                            <div className={styles.specialtiesGrid}>
                                {specialties.map((specialty, index) => (
                                    <div key={index} className={styles.specialtyItem}>
                                        <span className={styles.checkmark}>✓</span>
                                        <span>{specialty}</span>
                                    </div>
                                ))}
                            </div>
                            <p className={styles.specialtyNote}>
                                Don't see your specialty listed? We welcome all certified environmental health and safety professionals.
                            </p>
                            <div style={{ textAlign: 'center' }}>
                                <button className={styles.btnPrimary}>Get Started Today</button>
                            </div>
                        </div>
                    </section>

                    {/* Worker Taking Soil Sample Section */}
                    <section className={styles.imageTextSection}>
                        <div className={styles.imageTextContent}>
                            <div className={styles.imageTextImage}>
                                <div className={styles.imagePlaceholder}>Worker Taking Soil Sample</div>
                            </div>
                            <div className={styles.imageTextBox}>
                                <h2 className={styles.imageTextTitle}>Ready to Expand Your Impact?</h2>
                                <p className={styles.imageTextDescription}>
                                    Join thousands of environmental professionals who are making a difference while growing their business. Start connecting with clients who need your expertise today.
                                </p>
                                <button className={styles.becomeProviderButton}>Apply to Become a Provider</button>
                            </div>
                        </div>
                    </section>

                    {/* Provider Requirements Section */}
                    <section className={styles.requirementsSection}>
                        <div className={styles.sectionContainer}>
                            <h2 className={styles.sectionTitle}>Provider Requirements</h2>
                            <p className={styles.sectionIntro}>
                                To maintain our high standards, we require all providers to meet these criteria.
                            </p>
                            <div className={styles.requirementsGrid}>
                                {requirements.map((req, index) => (
                                    <div key={index} className={styles.requirementCard}>
                                        <div className={styles.requirementIcon}>{req.icon}</div>
                                        <h3 className={styles.requirementTitle}>{req.title}</h3>
                                        <p className={styles.requirementDescription}>{req.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};

export default BecameAProPage;